import { type JSX } from "react"
import {
  ChatItem,
  ChatSummary,
  CustomerMessage,
  InProgressMessage,
  Message,
  PersistentMessage,
  RecapMessage,
  TripLogItemChatItem,
  VoiceCallChatItem,
} from "@/types/chat"
import { EntityRef } from "@/types/entity"
import { MediaRef } from "@/types/media"
import { TripLogItem } from "@/types/trip"
import { PublicUser, UserRef } from "@/types/user"

import { assert, warnUnreachable } from "@/lib/utils/assertions"
import { minutesBetween } from "./date-utils"
import {
  isContinent,
  isCountry,
  isLocation,
  isProvince,
  isState,
} from "./entity-utils"
import { formatSerialListClause } from "./format-utils"

export function chatDisplayName(
  chatOrChatName: ChatSummary | string | undefined,
  tripIsUserCreated: boolean | undefined
) {
  const name =
    typeof chatOrChatName === "string" ? chatOrChatName : chatOrChatName?.name
  return name || (tripIsUserCreated ? "Untitled" : "New chat")
}

export function canEditGroupMessage(message: Message, currentUserRef: UserRef) {
  return (
    message.type === "customer" &&
    message.user?.user_ref === currentUserRef &&
    !mentionsMindtrip(message)
  )
}

export function mentionsMindtrip(message: CustomerMessage) {
  return message.markers.some(
    (marker) => marker.type === "user" && marker.special_type === "mindtrip"
  )
}

export function buildChatItemForMessage(
  message: PersistentMessage | RecapMessage
): ChatItem {
  return {
    type: "message",
    message,
    timestamp: message.timestamp,
  }
}

// Given a chronologically sorted list of log items, returns a list
// of ChatItems to represent them. It may return one ChatItem per
// log item or may collapse multiple consecutive log items into one.
export function buildChatItemsForTripLogItems(
  logItems: TripLogItem[]
): TripLogItemChatItem[] {
  const chatItems: TripLogItemChatItem[] = []

  const canCollapseLogItem = (
    previousChatItem: TripLogItemChatItem,
    logItem: TripLogItem
  ) => {
    const previousLogItem =
      previousChatItem.items[previousChatItem.items.length - 1]
    return (
      previousLogItem.user?.user_ref === logItem.user?.user_ref &&
      previousLogItem.kind === logItem.kind &&
      minutesBetween(previousLogItem.timestamp, logItem.timestamp) < 1 &&
      (previousLogItem.kind === "itinerary_event_created" ||
        previousLogItem.kind === "itinerary_event_deleted")
    )
  }

  for (const logItem of logItems) {
    const lastChatItem = chatItems[chatItems.length - 1]
    if (lastChatItem && canCollapseLogItem(lastChatItem, logItem)) {
      lastChatItem.items.push(logItem)
    } else {
      chatItems.push({
        type: "trip-log-item",
        items: [logItem],
        timestamp: logItem.timestamp,
      })
    }
  }

  return chatItems
}

// One record per voice call present in the run of messages, closing out the
// call's turns with its length and the anchor a rating is filed against.
//
// Grouped by call id rather than by adjacency: two calls back to back leave no
// typed message between their runs, and adjacency would report them as one long
// call. Not gated on the call being over: a collapsed call leaves the chat on
// screen, and ChatVoiceCallItem hides the live call's record until it ends.
export function buildChatItemsForVoiceCalls(
  messages: (PersistentMessage | RecapMessage)[]
): VoiceCallChatItem[] {
  // Only what a record reports, rather than each call's turns: where the call
  // began and the turn it ended on. startedAtMillis is absent for a call whose
  // turns predate the stamp, and openMillis is the fallback for that case — the
  // first turn, which undercounts by the connect and the unpersisted greeting
  // but still beats reporting no length at all.
  const calls = new Map<
    string,
    {
      openMillis: number
      startedAtMillis?: number
      lastTurn: PersistentMessage | RecapMessage
    }
  >()

  for (const message of messages) {
    const callId = message.voice_call_id
    if (!callId) continue
    const call = calls.get(callId)
    if (call) {
      call.lastTurn = message
      call.startedAtMillis ??= message.voice_call_started_at?.epoch_millis
    } else {
      calls.set(callId, {
        openMillis: message.timestamp.epoch_millis,
        startedAtMillis: message.voice_call_started_at?.epoch_millis,
        lastTurn: message,
      })
    }
  }

  return Array.from(calls, ([callId, call]) => {
    const elapsedMillis =
      call.lastTurn.timestamp.epoch_millis -
      (call.startedAtMillis ?? call.openMillis)

    return {
      type: "voice-call",
      // The final timestamp of the run it closes. Callers break the resulting
      // tie in this item's favour so it sorts below its own turns.
      timestamp: call.lastTurn.timestamp,
      callId,
      durationSeconds:
        elapsedMillis > 0 ? Math.round(elapsedMillis / 1000) : null,
      // A rating is filed against the last turn of the call so it can be traced
      // back to a chat; the backend only requires that the anchor be part of a
      // call, which every turn in this run is.
      anchorMessageId: call.lastTurn.id,
    }
  })
}

export function formatUsersTyping(
  users: PublicUser[],
  formatUser: (user: PublicUser) => string | JSX.Element
) {
  if (users.length === 0) return

  if (users.length === 1) {
    return <>{formatUser(users[0])} is typing</>
  } else if (users.length === 2) {
    return (
      <>
        {formatUser(users[0])} and {formatUser(users[1])} are typing
      </>
    )
  }

  return <>{"Several people are typing"}</>
}

export function formatUsersBotIsRespondingTo(
  users: PublicUser[],
  currentUserRef: UserRef,
  formatUser: (user: PublicUser) => string | JSX.Element
) {
  if (users.length === 0) return

  const userStrings = users.map((user) =>
    user.user_ref === currentUserRef ? "you" : formatUser(user)
  )

  if (userStrings.length === 1) {
    return <>Mindtrip is responding to {userStrings[0]}</>
  } else if (userStrings.length === 2) {
    const indexOfCurrentUser = users.findIndex(
      (u) => u.user_ref === currentUserRef
    )
    const indexOfUser1 = indexOfCurrentUser === -1 ? 0 : indexOfCurrentUser % 2
    const indexOfUser2 =
      indexOfCurrentUser === -1 ? 1 : (indexOfCurrentUser + 1) % 2
    return (
      // prettier-ignore
      <>
        Mindtrip is responding to {userStrings[indexOfUser1]} and {userStrings[indexOfUser2]}
      </>
    )
  }

  return <>Mindtrip is responding to several people</>
}

export function formatMessageForTripLogItems<T extends TripLogItem>(
  locale: string,
  logItems: T[],
  tripOwner: PublicUser | undefined,
  currentUserRef: UserRef,
  formatUser: (user: PublicUser) => string | JSX.Element,
  formatItineraryEvent: (id: number, title: string) => string | JSX.Element,
  formatMedia: (
    ref: MediaRef,
    type: string,
    title: string
  ) => string | JSX.Element
) {
  let message: string | JSX.Element | undefined
  const firstLogItem = logItems[0]
  let actingUser = firstLogItem.user
  const isCurrentUser = actingUser?.user_ref === currentUserRef
  const kind = firstLogItem.kind
  if (kind === "user_joined_trip") {
    if (isCurrentUser) {
      actingUser = tripOwner
      message = (
        <>
          You joined{" "}
          <span>
            {actingUser ? <>{formatUser(actingUser)}’s trip.</> : <>a trip.</>}
          </span>
        </>
      )
    } else {
      message = (
        <>
          <span>{actingUser ? formatUser(actingUser) : "Someone"}</span> joined
          this trip.
        </>
      )
    }
  } else if (kind === "user_left_trip") {
    message = (
      <>
        {isCurrentUser
          ? "You"
          : actingUser
            ? formatUser(actingUser)
            : "Someone"}{" "}
        <span>left this trip.</span>
      </>
    )
  } else if (
    kind === "itinerary_event_created" ||
    kind === "itinerary_event_deleted"
  ) {
    const itemsToName: T[] = logItems.slice(0, 3)
    const formattedItems = itemsToName.map((logItem) => {
      assert(
        logItem.kind === "itinerary_event_created" ||
          logItem.kind === "itinerary_event_deleted",
        "expected log item for creation or deletion of an itinerary event"
      )
      return formatItineraryEvent(
        logItem.itinerary_event_id,
        logItem.itinerary_event_title
      )
    })

    const unnamedItemCount = logItems.length - itemsToName.length
    if (unnamedItemCount > 0) {
      formattedItems.push(
        unnamedItemCount > 1 ? `${unnamedItemCount} others` : "one other"
      )
    }
    message =
      kind === "itinerary_event_created" ? (
        // added an idea or itinerary event
        <>
          {isCurrentUser
            ? "You"
            : actingUser
              ? formatUser(actingUser)
              : "Someone"}{" "}
          <span>
            added {formatSerialListClause(locale, formattedItems)} to the trip.
          </span>
        </>
      ) : (
        // deleted an idea or itinerary event
        <>
          {isCurrentUser
            ? "You"
            : actingUser
              ? formatUser(actingUser)
              : "Someone"}{" "}
          <span>
            removed {formatSerialListClause(locale, formattedItems)} from the
            trip.
          </span>
        </>
      )
  } else if (kind === "media_created" || kind === "media_deleted") {
    const itemsToName: T[] = logItems.slice(0, 3)
    const formattedItems = itemsToName.map((logItem) => {
      assert(
        logItem.kind === "media_created" || logItem.kind === "media_deleted",
        "expected log item for creation or deletion of a media"
      )
      return formatMedia(
        logItem.media_ref,
        logItem.media_type,
        logItem.media_title
      )
    })

    const unnamedItemCount = logItems.length - itemsToName.length
    if (unnamedItemCount > 0) {
      formattedItems.push(
        unnamedItemCount > 1 ? `${unnamedItemCount} others` : "one other"
      )
    }
    message =
      kind === "media_created" ? (
        <>
          {isCurrentUser
            ? "You"
            : actingUser
              ? formatUser(actingUser)
              : "Someone"}{" "}
          <span>
            added {formatSerialListClause(locale, formattedItems)} to media.
          </span>
        </>
      ) : (
        <>
          {isCurrentUser
            ? "You"
            : actingUser
              ? formatUser(actingUser)
              : "Someone"}{" "}
          <span>
            removed {formatSerialListClause(locale, formattedItems)} from media.
          </span>
        </>
      )
  } else if (kind === "share_chat") {
    message = (
      <>
        {actingUser ? formatUser(actingUser) : "Someone"}{" "}
        <span>shared this chat with you.</span>
      </>
    )
  } else if (kind === "share_trip") {
    message = (
      <>
        {actingUser ? formatUser(actingUser) : "Someone"}{" "}
        <span>shared this trip with you.</span>
      </>
    )
  } else {
    warnUnreachable(kind)
  }

  return { message, actingUser }
}

export function filterOutlierEntities(
  message: PersistentMessage | InProgressMessage
) {
  const outlierPlaceRefs = new Set<EntityRef>()

  message.markers
    .filter((marker) => marker.type === "place")
    .filter((marker) => marker.ref)
    .forEach((marker) => {
      const entity = message.places.find((e) => e.entity_ref === marker.ref)

      if (!entity || !isLocation(entity)) return

      if (
        marker.hide_pin ||
        isContinent(entity) ||
        isCountry(entity) ||
        (isState(entity) && entity.country_code === "US") ||
        (isProvince(entity) && entity.country_code === "CA")
      ) {
        if (marker.ref) {
          outlierPlaceRefs.add(marker.ref)
        }
        return
      }
    })

  return message.places.filter(
    (entity) => !outlierPlaceRefs.has(entity.entity_ref)
  )
}
